import React, { useState, useRef, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { mockData } from '../mock';

export const Hero = () => {
  const { hero } = mockData;
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-white overflow-hidden py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Right Side - Video (Shows first on mobile) */}
          <div className="relative animate-fade-in-up animation-delay-400 lg:order-2">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <video
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="w-full h-auto block"
                onLoadedData={() => {
                  console.log('Video loaded');
                  setVideoLoaded(true);
                  setVideoError(false);
                }}
                onError={(e) => {
                  console.error('Video failed to load:', e);
                  setVideoError(true);
                  setVideoLoaded(false);
                }}
                onCanPlay={() => {
                  console.log('Video can play');
                  if (videoRef.current) {
                    videoRef.current.play().catch(err => console.error('Play error:', err));
                  }
                }}
              >
                <source src={hero.videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              
              {/* Loading/Error placeholder - only shown when video hasn't loaded */}
              {(!videoLoaded || videoError) && (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl" style={{ minHeight: '400px' }}>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-[#2AD7C5]/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                      <svg className="w-8 h-8 text-[#2AD7C5] animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-gray-600 text-sm font-medium">
                      {videoError ? 'Grabo.ai Demo Video' : 'Loading Demo...'}
                    </p>
                  </div>
                </div>
              )}
            </div>
            {/* Decorative Elements */}
            <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-[#2AD7C5]/10 rounded-full blur-3xl -z-10"></div>
            <div className="absolute -top-6 -right-6 w-48 h-48 bg-[#2AD7C5]/10 rounded-full blur-3xl -z-10"></div>
          </div>

          {/* Left Content (Shows second on mobile) */}
          <div className="space-y-8 text-left lg:order-1">
            {/* Announcement Badge */}
            <div className="inline-flex items-center gap-2 bg-[#2AD7C5]/10 border border-[#2AD7C5]/30 rounded-full px-4 py-2 animate-fade-in">
              <div className="w-2 h-2 bg-[#2AD7C5] rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-gray-900">{hero.announcement}</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 tracking-tight animate-fade-in-up">
              {hero.title}
            </h1>

            {/* Subtitle */}
            <p className="text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed animate-fade-in-up animation-delay-200">
              {hero.subtitle}
            </p>

            {/* CTA Button */}
            <div className="animate-fade-in-up animation-delay-400">
              <Button
                onClick={scrollToContact}
                className="bg-[#2AD7C5] hover:bg-[#24c0b3] text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-200 hover:scale-105 shadow-lg shadow-[#2AD7C5]/20"
              >
                {hero.primaryCTA}
                <ChevronRight className="ml-2" size={20} />
              </Button>
            </div>

            {/* Audience Tags */}
            <div className="flex flex-wrap items-center gap-2 pt-4 animate-fade-in-up animation-delay-600">
              {hero.audienceTags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-full text-xs text-gray-700 font-medium hover:border-[#2AD7C5]/30 hover:bg-[#2AD7C5]/5 transition-colors"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-gray-300 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-gray-400 rounded-full animate-scroll"></div>
        </div>
      </div>
    </section>
  );
};
